import { useEffect, useMemo, useState } from "react";
import { api, getEffectiveDepartment } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BrandAvatar } from "@/components/BrandAvatar";
import LinkedAccountsPanel from "@/components/LinkedAccountsPanel";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BANK_ACCOUNTS } from "@/lib/bankAccounts";
import { Label } from "@/components/ui/label";
import { CountrySelect, WhatsAppInput } from "@/components/CountryFields";
import { flagEmoji, countryName } from "@/lib/countries";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StudentAttendanceDialog from "@/components/StudentAttendanceDialog";
import { Mail, Trash2, UserPlus, CalendarClock, Plus, Pencil, Eye, Briefcase, Globe2, MessageCircle, ExternalLink, CreditCard, Landmark, Wallet, CalendarDays, Copy, User2, Users2, Flag, MapPin, Globe, Sparkles, Target, AlertCircle, UserCircle, Phone, MessageSquareWarning, MessageSquare, CalendarCheck, Bell, BellOff, Search, X, Lock, Unlock, Receipt, Glasses, ShieldCheck, Radio } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import BulkImportStudents from "@/components/BulkImportStudents";
import EmailChangeCard from "@/components/EmailChangeCard";

function InviteFormFields({ inviteForm, setInviteForm, t }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>{t("name")}</Label>
        <Input
          data-testid="invite-name-input"
          value={inviteForm.name}
          onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
        />
      </div>
      <div>
        <Label>{t("email")}</Label>
        <Input
          type="email"
          data-testid="invite-email-input"
          value={inviteForm.email}
          onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
        />
      </div>
      <div>
        <Label>{t("country")}</Label>
        <CountrySelect
          testId="invite-country-select"
          value={inviteForm.country}
          onChange={(v) => setInviteForm({ ...inviteForm, country: v })}
        />
      </div>
      <div>
        <Label>{t("whatsapp")}</Label>
        <WhatsAppInput
          testIdDial="invite-whatsapp-dial"
          testIdNumber="invite-whatsapp-input"
          value={inviteForm.whatsapp}
          defaultCountry={inviteForm.country || "US"}
          onChange={(v) => setInviteForm({ ...inviteForm, whatsapp: v || "" })}
        />
      </div>
      {["teacher", "student"].includes(inviteForm.role) && (
      <div className="flex flex-col items-start">
        <Label>Escuela</Label>
        <div className="mt-1 h-10 inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-1"
             data-testid="invite-department-select">
          {[{ id: "dep_es", label: "Español" }, { id: "dep_en", label: "Inglés" }].map((d) => {
            const activo = (inviteForm.department_id || "dep_es") === d.id;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => setInviteForm({ ...inviteForm, department_id: d.id })}
                className={`px-3 h-full rounded text-sm font-medium transition-colors ${
                  activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`invite-dept-option-${d.id}`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>
      )}
      <p className="text-[11px] text-slate-500">{t("invite_user_hint")}</p>
    </div>
  );
}

export default function Users({ filterRole = null }) {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [openInvite, setOpenInvite] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewEnrollments, setViewEnrollments] = useState([]);
  const [loadingView, setLoadingView] = useState(false);
  const defaultInvite = () => ({
    name: "", email: "", role: filterRole || "student", whatsapp: "", country: "",
    department_id: getEffectiveDepartment() || "dep_es",
  });
  const defaultEdit = () => ({
    user_id: "", name: "", email: "", role: "student", whatsapp: "", country: "",
    date_of_birth: "", country_of_residence: "", city: "", occupation: "",
    native_language: "", how_did_you_hear: "", learning_goal: "",
    has_learning_difficulty: false, learning_difficulty_detail: "",
    address: "", profession: "", languages_spoken: [],
    emergency_contact_name: "", emergency_contact_phone: "", meet_link: "", zoom_link: "",
    student_status: "active",
    department_id: "dep_es",
    billing_name: "", billing_tax_id: "", billing_activity_code: "",
    billing_location: "", billing_email: "", guardian_email: "",
  });
  const [inviteForm, setInviteForm] = useState(defaultInvite());
  const [editForm, setEditForm] = useState(defaultEdit());
  const [inviting, setInviting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingFarewell, setSendingFarewell] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);
  const [studentStatusFilter, setStudentStatusFilter] = useState("active");
  const [enrollments, setEnrollments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  // Search + teacher filter (only meaningful when filterRole === "student")
  const [searchQuery, setSearchQuery] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("all"); // "all" | teacher_id
  const [regalo, setRegalo] = useState(null); // {user_id, name, months}

  const load = () => {
    const params = filterRole ? { role: filterRole } : {};
    if (filterRole === "student" && studentStatusFilter !== "all") {
      params.student_status = studentStatusFilter;
    }
    api.get("/users", { params }).then((r) => setUsers(r.data));
    api.get("/groups", { params: { include_archived: true } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    // Enrollments + teacher list power the teacher filter and student search
    // (a student "belongs to" every teacher whose group they're enrolled in).
    if (filterRole === "student") {
      api.get("/enrollments").then((r) => setEnrollments(r.data)).catch(() => setEnrollments([]));
      api.get("/users", { params: { role: "teacher" } }).then((r) => setTeachers(r.data)).catch(() => setTeachers([]));
    }
  };
  useEffect(() => { load(); }, [filterRole, studentStatusFilter]);

  // Sort users alphabetically by name (case-insensitive, accent-friendly Spanish
  // collation via `es` locale). The currently logged-in admin stays pinned to
  // the top for context; everyone else falls in A→Z order.
  const sortedUsers = useMemo(() => {
    // Build a student→teacher_ids map when we have enrollments + groups so we
    // can filter students by teacher without a backend round-trip.
    const groupsById = new Map(groups.map((g) => [g.id, g]));
    const studentToTeachers = new Map();
    for (const e of enrollments) {
      const g = groupsById.get(e.group_id);
      const tId = g?.teacher_id;
      if (!tId) continue;
      if (!studentToTeachers.has(e.student_id)) studentToTeachers.set(e.student_id, new Set());
      studentToTeachers.get(e.student_id).add(tId);
    }

    const q = (searchQuery || "").trim().toLowerCase();
    const list = users.filter((u) => {
      // Text search across name + email
      if (q) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.whatsapp || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Teacher filter — only meaningful for the students page
      if (teacherFilter !== "all" && filterRole === "student") {
        const set = studentToTeachers.get(u.user_id);
        if (!set || !set.has(teacherFilter)) return false;
      }
      return true;
    });

    const currentId = currentUser?.user_id;
    list.sort((a, b) => {
      if (a.user_id === currentId) return -1;
      if (b.user_id === currentId) return 1;
      return (a.name || a.email || "").localeCompare(
        b.name || b.email || "",
        "es",
        { sensitivity: "base" },
      );
    });
    return list;
  }, [users, currentUser?.user_id, searchQuery, teacherFilter, filterRole, enrollments, groups]);

  const updateRole = async (user_id, role) => {
    try {
      await api.put(`/users/${user_id}`, { role });
      toast.success(t("saved"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const toggleAccess = async (u) => {
    const blocking = !u.access_blocked;
    if (blocking && !window.confirm(`¿Bloquear el acceso de ${u.name || u.email}? No podrá iniciar sesión y se cerrarán sus sesiones abiertas.`)) return;
    try {
      await api.post(`/users/${u.user_id}/access`, { blocked: blocking });
      toast.success(blocking ? "Acceso bloqueado" : "Acceso habilitado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const viewAs = async (u) => {
    if (!window.confirm(`¿Ver la plataforma como ${u.name || u.email}? Es solo lectura por 30 minutos.`)) return;
    try {
      await api.post("/auth/impersonate/start", { user_id: u.user_id });
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };


  const cycleStudentStatus = async (u) => {
    // Superadmin quick-toggle from the row: active <-> inactive only.
    if (currentUser?.role !== "superadmin") return;
    const current = u.student_status || "active";
    const next = current === "active" ? "inactive" : "active";
    try {
      await api.put(`/users/${u.user_id}`, { student_status: next });
      toast.success(t("saved"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  // Modal de reactivación selectiva de programas: PENDIENTE DE AUTORIZACIÓN
  // (ver memory/pendiente_autorizacion.md). El backend ya soporta
  // `reactivate_enrollment_ids`, pero la interfaz no lo usa todavía.

  // Quick "Ver asistencia" from the row — picks the student's first active
  // enrollment and opens the StudentAttendanceDialog inline.
  const [attDialog, setAttDialog] = useState(null); // { enrollment, group }
  const openStudentAttendance = async (u) => {
    try {
      const [er, gr, pr] = await Promise.all([
        api.get(`/enrollments?student_id=${u.user_id}&with_consumption=true`),
        api.get("/groups"),
        api.get("/programs"),
      ]);
      const enrolls = Array.isArray(er.data) ? er.data : (er.data?.items || []);
      const active = enrolls.find((e) => !e.cancelled_at) || enrolls[0];
      if (!active) {
        toast.error("Este estudiante no tiene inscripciones");
        return;
      }
      const group = (gr.data || []).find((g) => g.id === active.group_id);
      const program = (pr.data || []).find((p) => p.id === active.program_id);
      // Enrollment override wins over program default (matches teacher view logic).
      const totalPerCycle =
        Number(active.classes_included) ||
        Number(program?.classes_included) ||
        8;
      setAttDialog({
        enrollment: active,
        group,
        // Normalize to what StudentAttendanceDialog expects: `student_id`
        student: { student_id: u.user_id, name: u.name, email: u.email },
        totalPerCycle,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  // Regalo de WSS Live al terminar el programa: misma cuenta, mismo historial.
  const regalarPase = async () => {
    try {
      const r = await api.post(`/live-pass/${regalo.user_id}/grant`, {
        months: Number(regalo.months) || 0, days: Number(regalo.days) || 0, notify: true,
      });
      toast.success(`Pase de WSS Live hasta ${r.data.trial_ends_on}`);
      setRegalo(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const deleteUser = async (user_id, email) => {
    try {
      await api.delete(`/users/${user_id}`);
      toast.success(`${t("deleted")}: ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const submitInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      setInviting(true);
      const payload = {
        name: inviteForm.name,
        email: inviteForm.email,
        role: inviteForm.role,
        whatsapp: inviteForm.whatsapp || null,
        country: inviteForm.country || null,
        department_id: inviteForm.department_id || "dep_es",
      };
      await api.post("/users/invite", payload);
      toast.success(`${t("invite_sent_to")} ${inviteForm.email}`);
      setOpenInvite(false);
      setInviteForm(defaultInvite());
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("error_generic");
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const programsMap = Object.fromEntries(programs.map((p) => [p.id, p]));
  const groupsMap = Object.fromEntries(groups.map((g) => [g.id, g]));
  const bankLabelFor = (value) => {
    if (!value) return null;
    const b = BANK_ACCOUNTS.find((x) => x.value === value);
    return b ? b.label : value;
  };

  // ----- View user dialog -----
  const openViewDialog = async (u) => {
    setViewUser(u);
    setViewEnrollments([]);
    setOpenView(true);
    if (u.role === "student") {
      try {
        setLoadingView(true);
        const r = await api.get("/enrollments", { params: { student_id: u.user_id } });
        setViewEnrollments(r.data || []);
      } catch {
        // Silent; show empty state
      } finally {
        setLoadingView(false);
      }
    }
  };

  // ----- Edit user dialog -----
  const openEditDialog = (u) => {
    setEditForm({
      user_id: u.user_id,
      name: u.name || "",
      email: u.email || "",
      role: u.role || "student",
      whatsapp: u.whatsapp || "",
      country: u.country || "",
      department_id: u.department_id || "dep_es",
      date_of_birth: u.date_of_birth || "",
      country_of_residence: u.country_of_residence || "",
      city: u.city || "",
      occupation: u.occupation || "",
      native_language: u.native_language || "",
      how_did_you_hear: u.how_did_you_hear || "",
      learning_goal: u.learning_goal || "",
      has_learning_difficulty: !!u.has_learning_difficulty,
      learning_difficulty_detail: u.learning_difficulty_detail || "",
      address: u.address || "",
      profession: u.profession || "",
      languages_spoken: u.languages_spoken || [],
      emergency_contact_name: u.emergency_contact_name || "",
      emergency_contact_phone: u.emergency_contact_phone || "",
      meet_link: u.meet_link || "",
      zoom_link: u.zoom_link || "",
      student_status: u.student_status || "active",
      is_test_account: !!u.is_test_account,
      spanish_level: u.spanish_level || "",
      billing_name: u.billing_name || "",
      billing_tax_id: u.billing_tax_id || "",
      billing_activity_code: u.billing_activity_code || "",
      billing_location: u.billing_location || "",
      billing_email: u.billing_email || "",
      guardian_email: u.guardian_email || "",
      unlimited_justified: !!u.unlimited_justified,
      unlimited_justified_note: u.unlimited_justified_note || "",
    });
    setOpenEdit(true);
  };
  const submitEdit = async () => {
    if (!editForm.name) { toast.error(t("error_generic")); return; }
    try {
      setSavingEdit(true);
      // Update basic fields + onboarding profile fields
      const basicUpd = {
        name: editForm.name,
        whatsapp: editForm.whatsapp || null,
        country: editForm.country || null,
        department_id: editForm.department_id || "dep_es",
        date_of_birth: editForm.date_of_birth || null,
        country_of_residence: editForm.country_of_residence || null,
      };
      if (currentUser?.role === "superadmin" && editForm.role) basicUpd.role = editForm.role;
      if (editForm.role === "student") {
        basicUpd.city = editForm.city || null;
        basicUpd.occupation = editForm.occupation || null;
        basicUpd.native_language = editForm.native_language || null;
        basicUpd.how_did_you_hear = editForm.how_did_you_hear || null;
        basicUpd.learning_goal = editForm.learning_goal || null;
        basicUpd.has_learning_difficulty = !!editForm.has_learning_difficulty;
        basicUpd.learning_difficulty_detail = editForm.has_learning_difficulty
          ? (editForm.learning_difficulty_detail || null)
          : null;
        basicUpd.student_status = editForm.student_status || "active";
        basicUpd.is_test_account = !!editForm.is_test_account;
        if (editForm.spanish_level) basicUpd.spanish_level = editForm.spanish_level;
        // Datos de facturación (empresa). Vacío = la factura sale a nombre del alumno.
        basicUpd.billing_name = editForm.billing_name || "";
        basicUpd.billing_tax_id = editForm.billing_tax_id || "";
        basicUpd.billing_activity_code = editForm.billing_activity_code || "";
        basicUpd.billing_location = editForm.billing_location || "";
        basicUpd.billing_email = editForm.billing_email || "";
        // Correo del padre/tutor: entra a esta cuenta y recibe copia de las facturas.
        basicUpd.guardian_email = editForm.guardian_email || "";
      } else if (editForm.role === "teacher") {
        basicUpd.address = editForm.address || null;
        basicUpd.profession = editForm.profession || null;
        basicUpd.languages_spoken = editForm.languages_spoken || [];
        basicUpd.emergency_contact_name = editForm.emergency_contact_name || null;
        basicUpd.emergency_contact_phone = editForm.emergency_contact_phone || null;
        basicUpd.meet_link = editForm.meet_link || null;
        basicUpd.zoom_link = editForm.zoom_link || null;
      } else if (["superadmin", "academic_director"].includes(editForm.role)) {
        basicUpd.meet_link = editForm.meet_link || null;
        basicUpd.zoom_link = editForm.zoom_link || null;
      }
      await api.put(`/users/${editForm.user_id}`, basicUpd);
      // El permiso de justificadas sin límite va por su propia puerta: solo lo
      // puede dar el super admin y queda registrado quién lo dio.
      if (currentUser?.role === "superadmin" && editForm.role === "student") {
        const before = users.find((x) => x.user_id === editForm.user_id) || {};
        const changed = !!before.unlimited_justified !== !!editForm.unlimited_justified
          || (before.unlimited_justified_note || "") !== (editForm.unlimited_justified_note || "");
        if (changed) {
          await api.put(`/users/${editForm.user_id}/absence-privilege`, {
            enabled: !!editForm.unlimited_justified,
            note: editForm.unlimited_justified_note || "",
          });
        }
      }
      toast.success(t("saved"));
      setOpenEdit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSavingEdit(false);
    }
  };

  const resendMagic = async (email) => {
    try {
      await api.post("/auth/magic-link/request", { email });
      toast.success(`${t("invite_sent_to")} ${email}`);
    } catch { toast.error(t("error_generic")); }
  };

  const pageTitle = filterRole === "teacher" ? t("teachers")
    : filterRole === "student" ? t("students")
    : t("users");
  const pageHint = filterRole === "teacher" ? t("teachers_invite_hint")
    : filterRole === "student" ? t("students_invite_hint")
    : t("users_invite_hint");

  return (
    <div>
      <PageHeader
        title={pageTitle}
        subtitle={pageHint}
        testId="users-header"
        action={
          <div className="flex items-center gap-2">
            {currentUser?.role !== "teacher" && (!filterRole || filterRole === "teacher") && (
              <Dialog
                open={openInvite && inviteForm.role === "teacher"}
                onOpenChange={(o) => {
                  if (o) {
                    setInviteForm({ ...defaultInvite(), role: "teacher" });
                    setOpenInvite(true);
                  } else {
                    setOpenInvite(false);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="invite-teacher-button" variant={filterRole === "teacher" ? undefined : "outline"} className={filterRole === "teacher" ? "btn-brand" : undefined}>
                    <UserPlus className="h-4 w-4" /> {t("invite_teacher")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{t("invite_teacher")}</DialogTitle></DialogHeader>
                  <InviteFormFields inviteForm={inviteForm} setInviteForm={setInviteForm} t={t} />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenInvite(false)}>{t("cancel")}</Button>
                    <Button data-testid="invite-submit-button" onClick={submitInvite} disabled={inviting || !inviteForm.name || !inviteForm.email}>
                      {inviting ? t("loading") : t("invite_teacher")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {currentUser?.role !== "teacher" && (!filterRole || filterRole === "student") && (
              <Dialog
                open={openInvite && inviteForm.role === "student"}
                onOpenChange={(o) => {
                  if (o) {
                    setInviteForm({ ...defaultInvite(), role: "student" });
                    setOpenInvite(true);
                  } else {
                    setOpenInvite(false);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="invite-student-button" className="btn-brand">
                    <UserPlus className="h-4 w-4" /> {t("invite_student")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{t("invite_student")}</DialogTitle></DialogHeader>
                  <InviteFormFields inviteForm={inviteForm} setInviteForm={setInviteForm} t={t} />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenInvite(false)}>{t("cancel")}</Button>
                    <Button data-testid="invite-submit-button" onClick={submitInvite} disabled={inviting || !inviteForm.name || !inviteForm.email}>
                      {inviting ? t("loading") : t("invite_student")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {currentUser?.role !== "teacher" && (!filterRole || filterRole === "student") && (
              <BulkImportStudents onImported={load} />
            )}
          </div>
        }
      />

      {filterRole === "student" && (
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-1.5" data-testid="student-status-filter">
            {[
              { v: "active", label: t("status_active"), color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
              { v: "inactive", label: t("status_inactive"), color: "bg-amber-100 text-amber-700 border-amber-200" },
              { v: "cancelled", label: t("status_cancelled"), color: "bg-red-100 text-red-700 border-red-200" },
              { v: "all", label: t("all"), color: "bg-slate-200 text-slate-800 border-slate-300" },
            ].map((opt) => {
              const active = studentStatusFilter === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setStudentStatusFilter(opt.v)}
                  data-testid={`status-filter-${opt.v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    active ? opt.color + " ring-2 ring-offset-1 ring-slate-300" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Search box */}
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid="students-search-input"
              type="search"
              placeholder={t("students_search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 h-9"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="students-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100"
                aria-label={t("clear")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Teacher filter — only for admins (teachers already see only their students) */}
          {currentUser?.role !== "teacher" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">{t("teacher")}:</Label>
              <Select value={teacherFilter} onValueChange={setTeacherFilter}>
                <SelectTrigger data-testid="students-teacher-filter" className="h-9 min-w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  {teachers.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })).map((tt) => (
                    <SelectItem key={tt.user_id} value={tt.user_id}>{tt.name || tt.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(searchQuery || teacherFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearchQuery(""); setTeacherFilter("all"); }}
              data-testid="students-filters-reset"
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" /> {t("clear_filters")}
            </Button>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Access status legend */}
        <div className="flex items-center gap-4 px-4 py-2 text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50/60">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Ya ingresó a la plataforma
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            Nunca ha ingresado
          </span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead className="w-48">{t("role")}</TableHead>
              <TableHead className="w-32 text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">
                {(searchQuery || teacherFilter !== "all") ? t("no_results_for_filter") : t("no_data")}
              </TableCell></TableRow>
            )}
            {sortedUsers.length === 0 ? null : sortedUsers.map((u) => {
              const initials = (u.name || u.email).slice(0, 2).toUpperCase();
              const isSelf = u.user_id === currentUser?.user_id;
              return (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {(() => {
                        // El backend manda `has_accessed`: ya entró alguna vez
                        // (ingreso real, políticas aceptadas u onboarding hecho).
                        // Antes se usaba `profile_completed`, que solo se llena
                        // con el formulario de bienvenida — los del pase WSS GO
                        // nunca pasan por ahí y quedaban en rojo para siempre.
                        const accessed = !!u.has_accessed;
                        return (
                          <span
                            className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-white ${
                              accessed ? "bg-emerald-500" : "bg-red-500"
                            }`}
                            title={
                              accessed
                                ? `Ya ingresó a la plataforma${u.last_login_at ? ` · último ingreso ${u.last_login_at.slice(0, 10)}` : ""}`
                                : "Nunca ha ingresado"
                            }
                            aria-label={
                              accessed ? "Ya ingresó a la plataforma" : "Nunca ha ingresado"
                            }
                            data-testid={`access-dot-${u.user_id}`}
                          />
                        );
                      })()}
                      <BrandAvatar src={u.picture} alt={u.name} className="h-8 w-8" />
                      <span className="font-medium">
                        {u.name}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-blue">({t("you")})</span>}
                      </span>
                      {u.access_blocked && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700"
                          title="No puede iniciar sesión"
                          data-testid={`blocked-badge-${u.user_id}`}
                        >
                          <Lock className="h-3 w-3" /> Acceso bloqueado
                        </span>
                      )}
                      {u.is_test_account && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600"
                          title="No entra en ninguna estadística"
                          data-testid={`test-account-badge-${u.user_id}`}
                        >
                          Prueba
                        </span>
                      )}
                      {u.role === "student" && (() => {
                        const s = u.student_status || "active";
                        const cfg = {
                          active: "bg-emerald-100 text-emerald-700",
                          inactive: "bg-amber-100 text-amber-700",
                          cancelled: "bg-red-100 text-red-700",
                        }[s];
                        const canToggle = currentUser?.role === "superadmin";
                        return (
                          <button
                            type="button"
                            onClick={() => canToggle && cycleStudentStatus(u)}
                            disabled={!canToggle}
                            className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg} ${canToggle ? "hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 cursor-pointer" : "cursor-default"}`}
                            title={canToggle ? "Click para cambiar el estado" : ""}
                            data-testid={`student-status-badge-${u.user_id}`}
                          >
                            {t(`status_${s}`)}
                          </button>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    {currentUser?.role === "superadmin" ? (
                      <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)} disabled={isSelf}>
                        <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="superadmin">{t("superadmin")}</SelectItem>
                          <SelectItem value="academic_director">{t("academic_director")}</SelectItem>
                          <SelectItem value="admissions_lead_coordinator">{t("admissions_lead_coordinator")}</SelectItem>
                          <SelectItem value="teacher">{t("teacher")}</SelectItem>
                          <SelectItem value="student">{t("student")}</SelectItem>
                          <SelectItem value="affiliate">{t("affiliate")}</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700" data-testid={`role-label-${u.user_id}`}>
                        {t(u.role)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-600 hover:bg-slate-100"
                        title={t("view_details")}
                        onClick={() => openViewDialog(u)}
                        data-testid={`view-user-${u.user_id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {currentUser?.role !== "teacher" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:bg-slate-100"
                          title={t("edit")}
                          onClick={() => openEditDialog(u)}
                          data-testid={`edit-user-${u.user_id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {u.role === "student" && ["teacher","superadmin","academic_director"].includes(currentUser?.role) && (
                        <a
                          href="/dashboard/messages"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.assign("/dashboard/messages");
                          }}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-700 hover:bg-emerald-50"
                          title={`Chat con ${u.name || u.email}`}
                          data-testid={`dm-user-${u.user_id}`}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {u.role === "student" && (
                        <button
                          type="button"
                          onClick={() => openStudentAttendance(u)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-600 hover:bg-slate-100"
                          title="Ver asistencia"
                          data-testid={`view-attendance-${u.user_id}`}
                        >
                          <CalendarCheck className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {u.email && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(u.email);
                              toast.success(`Correo copiado: ${u.email}`);
                            } catch { /* no-op */ }
                            window.location.href = `mailto:${u.email}`;
                          }}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-brand-blue hover:bg-brand-blue/10"
                          title={`Enviar correo a ${u.email}`}
                          data-testid={`mailto-user-${u.user_id}`}
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Onboarding reminder cadence toggle — only visible for users
                          who haven't completed onboarding yet. Yellow bell = active,
                          gray muted bell = paused by admin. */}
                      {u.profile_completed === false && (u.role === "student" || u.role === "teacher") && (() => {
                        const isPaused = !!u.reminder_paused_at;
                        return (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const r = await api.post(`/users/${u.user_id}/onboarding-reminders/toggle`);
                                toast.success(r.data.paused
                                  ? "Recordatorios pausados"
                                  : "Recordatorios reactivados (cada 2 días)");
                                load();
                              } catch { toast.error(t("error_generic")); }
                            }}
                            className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${
                              isPaused ? "text-slate-400 hover:bg-slate-100" : "text-amber-600 hover:bg-amber-50"
                            }`}
                            title={isPaused
                              ? "Recordatorios pausados — clic para reactivar"
                              : "Recordatorio automático activo (cada 2 días hasta que acepte T&C). Clic para pausar."}
                            data-testid={`toggle-reminders-${u.user_id}`}
                          >
                            {isPaused ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                          </button>
                        );
                      })()}
                      {u.whatsapp && (
                        <a
                          href={`https://wa.me/${(u.whatsapp || "").replace(/[^\d]/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-600 hover:bg-emerald-50"
                          title={`WhatsApp ${u.whatsapp}`}
                          data-testid={`whatsapp-user-${u.user_id}`}
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {!isSelf && currentUser?.role === "superadmin" && u.role !== "superadmin" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-brand-blue hover:bg-brand-blue/10"
                          onClick={() => viewAs(u)}
                          title={`Ver la plataforma como ${u.name || u.email} (solo lectura)`}
                          data-testid={`impersonate-${u.user_id}`}
                        >
                          <Glasses className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isSelf && currentUser?.role === "superadmin" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={u.access_blocked ? "text-emerald-700 hover:bg-emerald-50" : "text-amber-700 hover:bg-amber-50"}
                          onClick={() => toggleAccess(u)}
                          title={u.access_blocked ? "Habilitar acceso" : "Bloquear acceso a la plataforma"}
                          data-testid={`toggle-access-${u.user_id}`}
                        >
                          {u.access_blocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                      {!isSelf && currentUser?.role === "superadmin" && u.role === "student" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-teal-700 hover:bg-teal-50"
                          onClick={() => setRegalo({ user_id: u.user_id, name: u.name || u.email, days: 0, months: 1 })}
                          title="Regalarle meses de WSS Live al terminar su programa"
                          data-testid={`grant-live-pass-${u.user_id}`}
                        >
                          <Radio className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isSelf && currentUser?.role !== "teacher" && (
                        <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("delete_user_title")}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t("delete_user_desc").replace("{name}", u.name || u.email)}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteUser(u.user_id, u.email)}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid={`confirm-delete-user-${u.user_id}`}
                            >
                              {t("delete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit user dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("edit_user")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("name")}</Label>
              <Input
                data-testid="edit-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="edit-email-input" value={editForm.email} disabled className="bg-slate-100 text-slate-500" />
              <div className="mt-2">
                <EmailChangeCard
                  userId={editForm.user_id}
                  currentEmail={editForm.email}
                  onChanged={async () => { await load(); setOpenEdit(false); }}
                />
              </div>
            </div>
            <div>
              <Label>{t("role")}</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                disabled={currentUser?.role !== "superadmin"}
              >
                <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="superadmin">{t("superadmin")}</SelectItem>
                  <SelectItem value="academic_director">{t("academic_director")}</SelectItem>
                  <SelectItem value="admissions_lead_coordinator">{t("admissions_lead_coordinator")}</SelectItem>
                  <SelectItem value="teacher">{t("teacher")}</SelectItem>
                  <SelectItem value="student">{t("student")}</SelectItem>
                  <SelectItem value="affiliate">{t("affiliate")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {["teacher", "student"].includes(editForm.role) && (
            <div className="flex flex-col items-start">
              <Label>Escuela</Label>
              <div className="mt-1 h-10 inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-1"
                   data-testid="edit-department-select">
                {[{ id: "dep_es", label: "Español" }, { id: "dep_en", label: "Inglés" }].map((d) => {
                  const activo = (editForm.department_id || "dep_es") === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, department_id: d.id })}
                      className={`px-3 h-full rounded text-sm font-medium transition-colors ${
                        activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                      data-testid={`edit-dept-option-${d.id}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
            <div>
              <Label>{t("country")}</Label>
              <CountrySelect
                testId="edit-country-select"
                value={editForm.country}
                onChange={(v) => setEditForm({ ...editForm, country: v })}
              />
            </div>
            <div>
              <Label>{t("whatsapp")}</Label>
              <WhatsAppInput
                testIdDial="edit-whatsapp-dial"
                testIdNumber="edit-whatsapp-input"
                value={editForm.whatsapp}
                defaultCountry={editForm.country || "US"}
                onChange={(v) => setEditForm({ ...editForm, whatsapp: v || "" })}
              />
            </div>

            {(editForm.role === "student" || editForm.role === "teacher") && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid="edit-profile-section">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <User2 className="h-3.5 w-3.5" />
                  {t("edit_profile_section_title")}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t("field_date_of_birth")}</Label>
                    <Input
                      type="date"
                      data-testid="edit-dob-input"
                      value={editForm.date_of_birth}
                      onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t("field_country_of_residence")}</Label>
                    <CountrySelect
                      testId="edit-country-of-residence-select"
                      value={editForm.country_of_residence}
                      onChange={(v) => setEditForm({ ...editForm, country_of_residence: v })}
                    />
                  </div>
                </div>

                {editForm.role === "student" && (
                  <>
                    <div>
                      <Label className="text-xs">{t("spanish_level")}</Label>
                      <Select
                        value={editForm.spanish_level || ""}
                        onValueChange={(v) => setEditForm({ ...editForm, spanish_level: v })}
                      >
                        <SelectTrigger data-testid="edit-spanish-level-select" className="mt-1.5">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {["A1", "A2", "B1", "B2", "C1"].map((lv) => (
                            <SelectItem key={lv} value={lv}>{lv}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t("student_status")}</Label>
                      <Select
                        value={editForm.student_status || "active"}
                        onValueChange={(v) => setEditForm({ ...editForm, student_status: v })}
                      >
                        <SelectTrigger data-testid="edit-student-status-select" className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t("status_active")}</SelectItem>
                          <SelectItem value="inactive">{t("status_inactive")}</SelectItem>
                          <SelectItem value="cancelled">{t("status_cancelled")}</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* Cuenta de prueba: no entra en ninguna estadística. */}
                      <label className="mt-2 flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!editForm.is_test_account}
                          onChange={(ev) => setEditForm({ ...editForm, is_test_account: ev.target.checked })}
                          data-testid="edit-test-account-checkbox"
                          className="mt-0.5"
                        />
                        <span>
                          Cuenta de prueba
                          <span className="block text-slate-500">
                            No entra en ninguna estadística ni reporte; solo se cuenta como cuenta de prueba.
                          </span>
                        </span>
                      </label>
                      {(editForm.student_status === "cancelled") && (
                        <div className="mt-2 flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200">
                          <MessageSquareWarning className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-amber-800 leading-snug mb-2">
                              {t("churn_send_farewell_btn")} — {t("churn_feedback_intro")}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid="send-farewell-email-btn"
                              disabled={sendingFarewell}
                              onClick={async () => {
                                try {
                                  setSendingFarewell(true);
                                  await api.post(`/users/${editForm.user_id}/farewell-email`);
                                  toast.success(t("churn_farewell_sent_toast"));
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || t("churn_farewell_error_toast"));
                                } finally {
                                  setSendingFarewell(false);
                                }
                              }}
                              className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                            >
                              <Mail className="h-3 w-3 mr-1.5" />
                              {t("churn_send_farewell_btn")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60" data-testid="billing-profile-section">
                      <div className="flex items-center gap-2 mb-1">
                        <Receipt className="h-4 w-4 text-brand-blue" />
                        <span className="text-sm font-semibold text-slate-900">{t("billing_profile")}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-3">{t("billing_profile_hint")}</p>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">{t("billing_name")}</Label>
                          <Input type="text" data-testid="edit-billing-name-input" className="mt-1.5"
                            placeholder="Ej. Intel Costa Rica S.A."
                            value={editForm.billing_name}
                            onChange={(e) => setEditForm({ ...editForm, billing_name: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">{t("billing_tax_id")}</Label>
                            <Input type="text" data-testid="edit-billing-tax-id-input" className="mt-1.5"
                              value={editForm.billing_tax_id}
                              onChange={(e) => setEditForm({ ...editForm, billing_tax_id: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">{t("billing_activity_code")}</Label>
                            <Input type="text" data-testid="edit-billing-activity-input" className="mt-1.5"
                              value={editForm.billing_activity_code}
                              onChange={(e) => setEditForm({ ...editForm, billing_activity_code: e.target.value })} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">{t("billing_location")}</Label>
                          <Input type="text" data-testid="edit-billing-location-input" className="mt-1.5"
                            value={editForm.billing_location}
                            onChange={(e) => setEditForm({ ...editForm, billing_location: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">{t("billing_email")}</Label>
                          <Input type="email" data-testid="edit-billing-email-input" className="mt-1.5"
                            placeholder="contabilidad@empresa.com"
                            value={editForm.billing_email}
                            onChange={(e) => setEditForm({ ...editForm, billing_email: e.target.value })} />
                          <p className="text-[11px] text-slate-500 mt-1">{t("billing_email_hint")}</p>
                        </div>
                      </div>
                    </div>
                    {currentUser?.role === "superadmin" && (
                      <div className="rounded-lg border border-amber-200 p-3 bg-amber-50/60"
                           data-testid="unlimited-justified-section">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-semibold text-slate-900">{t("unlimited_justified")}</span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1">{t("unlimited_justified_hint")}</p>
                          </div>
                          <Switch
                            checked={!!editForm.unlimited_justified}
                            onCheckedChange={(v) => setEditForm({ ...editForm, unlimited_justified: v })}
                            data-testid="edit-unlimited-justified-switch"
                          />
                        </div>
                        {editForm.unlimited_justified && (
                          <div className="mt-2">
                            <Label className="text-xs">{t("unlimited_justified_note_label")}</Label>
                            <Input className="mt-1.5" data-testid="edit-unlimited-justified-note"
                              placeholder="Ej.: estudiante desde 2019, se le da flexibilidad"
                              value={editForm.unlimited_justified_note || ""}
                              onChange={(e) => setEditForm({ ...editForm, unlimited_justified_note: e.target.value })} />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60" data-testid="guardian-section">                      <div className="flex items-center gap-2 mb-1">
                        <Users2 className="h-4 w-4 text-brand-blue" />
                        <span className="text-sm font-semibold text-slate-900">{t("guardian_title")}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-3">{t("guardian_hint")}</p>
                      <Label className="text-xs">{t("guardian_email")}</Label>
                      <Input type="email" data-testid="edit-guardian-email-input" className="mt-1.5"
                        placeholder="mama@correo.com"
                        value={editForm.guardian_email}
                        onChange={(e) => setEditForm({ ...editForm, guardian_email: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">{t("field_city")}</Label>
                        <Input
                          type="text"
                          data-testid="edit-city-input"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t("field_occupation")}</Label>
                        <Input
                          type="text"
                          data-testid="edit-occupation-input"
                          value={editForm.occupation}
                          onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })}
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_native_language")}</Label>
                      <Input
                        type="text"
                        data-testid="edit-native-language-input"
                        value={editForm.native_language}
                        onChange={(e) => setEditForm({ ...editForm, native_language: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_how_did_you_hear")}</Label>
                      <Select
                        value={editForm.how_did_you_hear || "__none__"}
                        onValueChange={(v) => setEditForm({ ...editForm, how_did_you_hear: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger data-testid="edit-how-select" className="mt-1.5">
                          <SelectValue placeholder={t("pick_option")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("pick_option")}</SelectItem>
                          {["online_search","facebook_group","newspaper_ads","google_ads","facebook_ads","recommendation","street_signal","banner","flyers","walk_in","another_school","webpage_ads"].map((o) => (
                            <SelectItem key={o} value={o}>{t(`how_${o}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_learning_goal")}</Label>
                      <textarea
                        data-testid="edit-goal-input"
                        rows={2}
                        value={editForm.learning_goal}
                        onChange={(e) => setEditForm({ ...editForm, learning_goal: e.target.value })}
                        className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                  </>
                )}

                {editForm.role === "teacher" && (
                  <>
                    <div>
                      <Label className="text-xs">{t("field_address")}</Label>
                      <Input
                        type="text"
                        data-testid="edit-address-input"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_profession")}</Label>
                      <Input
                        type="text"
                        data-testid="edit-profession-input"
                        value={editForm.profession}
                        onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_languages_spoken")}</Label>
                      <Input
                        type="text"
                        data-testid="edit-languages-input"
                        placeholder="español, inglés, ..."
                        value={(editForm.languages_spoken || []).join(", ")}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          languages_spoken: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                        })}
                        className="mt-1.5"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">{t("field_languages_spoken_hint")}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">{t("field_emergency_contact_name")}</Label>
                        <Input
                          type="text"
                          data-testid="edit-ec-name-input"
                          value={editForm.emergency_contact_name}
                          onChange={(e) => setEditForm({ ...editForm, emergency_contact_name: e.target.value })}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t("field_emergency_contact_phone")}</Label>
                        <Input
                          type="tel"
                          data-testid="edit-ec-phone-input"
                          value={editForm.emergency_contact_phone}
                          onChange={(e) => setEditForm({ ...editForm, emergency_contact_phone: e.target.value })}
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_meet_link")}</Label>
                      <Input
                        type="url"
                        placeholder="https://meet.google.com/..."
                        data-testid="edit-meet-link-input"
                        value={editForm.meet_link}
                        onChange={(e) => setEditForm({ ...editForm, meet_link: e.target.value })}
                        className="mt-1.5"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">{t("field_meet_link_hint")}</p>
                    </div>
                    <div>
                      <Label className="text-xs">{t("field_zoom_link")}</Label>
                      <Input
                        type="url"
                        placeholder="https://us02web.zoom.us/j/..."
                        data-testid="edit-zoom-link-input"
                        value={editForm.zoom_link}
                        onChange={(e) => setEditForm({ ...editForm, zoom_link: e.target.value })}
                        className="mt-1.5"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">{t("field_zoom_link_hint")}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {(editForm.role === "superadmin" || editForm.role === "academic_director") && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid="edit-meeting-room-section">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("meeting_room_section")}
                </div>
                <div>
                  <Label className="text-xs">{t("field_meet_link")}</Label>
                  <Input
                    type="url"
                    placeholder="https://meet.google.com/..."
                    data-testid="edit-room-meet-input"
                    value={editForm.meet_link}
                    onChange={(e) => setEditForm({ ...editForm, meet_link: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("field_zoom_link")}</Label>
                  <Input
                    type="url"
                    placeholder="https://us02web.zoom.us/j/..."
                    data-testid="edit-room-zoom-input"
                    value={editForm.zoom_link}
                    onChange={(e) => setEditForm({ ...editForm, zoom_link: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
            )}

          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpenEdit(false)}>{t("cancel")}</Button>
            {editForm.email && (
              <Button
                type="button"
                variant="outline"
                onClick={() => resendMagic(editForm.email)}
                data-testid="resend-magic-link-btn"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {t("resend_magic_link")}
              </Button>
            )}
            {editForm.role === "student" && editForm.user_id && (
              <Button
                type="button"
                variant="outline"
                data-testid="resend-welcome-email-btn"
                disabled={resendingWelcome}
                onClick={async () => {
                  try {
                    setResendingWelcome(true);
                    await api.post(`/users/${editForm.user_id}/welcome-email/resend`);
                    toast.success(t("welcome_email_resent"));
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || t("welcome_email_resend_error"));
                  } finally {
                    setResendingWelcome(false);
                  }
                }}
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {t("resend_welcome_email")}
              </Button>
            )}
            <Button data-testid="edit-submit-button" onClick={submitEdit} disabled={savingEdit || !editForm.name}>
              {savingEdit ? t("loading") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View user dialog (read-only) */}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="view-user-dialog">
          {viewUser && (
            <>
              {/* Gradient header with avatar */}
              <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-orange-50 via-white to-blue-50 border-b border-slate-100">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-sm uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    {t("view_user")}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex items-center gap-4">
                  <BrandAvatar src={viewUser.picture} alt={viewUser.name} className="h-16 w-16 ring-4 ring-white shadow-sm" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-xl text-slate-900 truncate leading-tight" data-testid="view-user-name">
                      {viewUser.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(viewUser.email);
                        toast.success(t("copied_to_clipboard"));
                      }}
                      className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-blue transition-colors mt-1 group"
                      data-testid="view-user-email"
                      title={t("copy_email")}
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{viewUser.email}</span>
                      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5">
                {/* Contact info cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <InfoCard
                    icon={<Briefcase className="h-3.5 w-3.5" />}
                    label={t("role")}
                    testId="view-user-role"
                  >
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${roleBadgeClasses(viewUser.role)}`}>
                      {t(viewUser.role) || viewUser.role}
                    </span>
                  </InfoCard>
                  <InfoCard
                    icon={<Globe2 className="h-3.5 w-3.5" />}
                    label={t("country")}
                    testId="view-user-country"
                  >
                    {viewUser.country ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-base leading-none">{flagEmoji(viewUser.country)}</span>
                        <span>{countryName(viewUser.country)}</span>
                      </span>
                    ) : <em className="text-slate-400 not-italic">{t("no_country")}</em>}
                  </InfoCard>
                  <InfoCard
                    icon={<MessageCircle className="h-3.5 w-3.5" />}
                    label={t("whatsapp")}
                    testId="view-user-whatsapp"
                  >
                    {viewUser.whatsapp ? (
                      <a
                        href={`https://wa.me/${viewUser.whatsapp.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-slate-800 hover:text-emerald-600 transition-colors"
                      >
                        {viewUser.whatsapp}
                        <ExternalLink className="h-3 w-3 opacity-50" />
                      </a>
                    ) : <em className="text-slate-400 not-italic">{t("no_whatsapp")}</em>}
                  </InfoCard>
                </div>

                {/* Onboarding profile (role-aware) */}
                {(viewUser.role === "student" || viewUser.role === "teacher") && (
                  <div data-testid="view-user-profile">
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="h-px flex-1 bg-slate-200" />
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <User2 className="h-3.5 w-3.5" />
                        {t("view_profile_section")}
                      </div>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    {!viewUser.profile_completed && !viewUser.date_of_birth ? (
                      <div className="text-center py-5 px-4 bg-amber-50/60 rounded-lg border border-dashed border-amber-200">
                        <p className="text-xs text-amber-800 italic">{t("view_no_profile_data")}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <InfoCard icon={<CalendarDays className="h-3 w-3" />} label={t("field_date_of_birth")} testId="view-dob">
                          {viewUser.date_of_birth || <em className="text-slate-400 not-italic">—</em>}
                        </InfoCard>
                        <InfoCard icon={<Flag className="h-3 w-3" />} label={t("field_country_of_residence")} testId="view-country-of-residence">
                          {viewUser.country_of_residence ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-base leading-none">{flagEmoji(viewUser.country_of_residence)}</span>
                              <span>{countryName(viewUser.country_of_residence)}</span>
                            </span>
                          ) : <em className="text-slate-400 not-italic">—</em>}
                        </InfoCard>
                        {viewUser.role === "student" && (
                          <>
                            <InfoCard icon={<MapPin className="h-3 w-3" />} label={t("field_city")} testId="view-city">
                              {viewUser.city || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<Briefcase className="h-3 w-3" />} label={t("field_occupation")} testId="view-occupation">
                              {viewUser.occupation || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<Globe className="h-3 w-3" />} label={t("field_native_language")} testId="view-native-language">
                              {viewUser.native_language || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<Sparkles className="h-3 w-3" />} label={t("field_how_did_you_hear")} testId="view-how">
                              {viewUser.how_did_you_hear ? t(`how_${viewUser.how_did_you_hear}`) : <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <div className="col-span-2">
                              <InfoCard icon={<Target className="h-3 w-3" />} label={t("field_learning_goal")} testId="view-goal">
                                {viewUser.learning_goal ? (
                                  <p className="whitespace-pre-wrap text-xs leading-relaxed">{viewUser.learning_goal}</p>
                                ) : <em className="text-slate-400 not-italic">—</em>}
                              </InfoCard>
                            </div>
                            {viewUser.has_learning_difficulty && (
                              <div className="col-span-2">
                                <InfoCard icon={<AlertCircle className="h-3 w-3" />} label={t("field_has_learning_difficulty")} testId="view-difficulty">
                                  <p className="whitespace-pre-wrap text-xs leading-relaxed">
                                    {viewUser.learning_difficulty_detail || t("yes")}
                                  </p>
                                </InfoCard>
                              </div>
                            )}
                          </>
                        )}
                        {viewUser.role === "teacher" && (
                          <>
                            <div className="col-span-2">
                              <InfoCard icon={<MapPin className="h-3 w-3" />} label={t("field_address")} testId="view-address">
                                {viewUser.address || <em className="text-slate-400 not-italic">—</em>}
                              </InfoCard>
                            </div>
                            <InfoCard icon={<Briefcase className="h-3 w-3" />} label={t("field_profession")} testId="view-profession">
                              {viewUser.profession || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<Globe className="h-3 w-3" />} label={t("field_languages_spoken")} testId="view-languages">
                              {(viewUser.languages_spoken && viewUser.languages_spoken.length > 0) ? (
                                <div className="flex flex-wrap gap-1">
                                  {viewUser.languages_spoken.map((l) => (
                                    <span key={l} className="px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-[11px] font-medium">
                                      {t(`lang_${l}`) !== `lang_${l}` ? t(`lang_${l}`) : l}
                                    </span>
                                  ))}
                                </div>
                              ) : <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<UserCircle className="h-3 w-3" />} label={t("field_emergency_contact_name")} testId="view-ec-name">
                              {viewUser.emergency_contact_name || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                            <InfoCard icon={<Phone className="h-3 w-3" />} label={t("field_emergency_contact_phone")} testId="view-ec-phone">
                              {viewUser.emergency_contact_phone || <em className="text-slate-400 not-italic">—</em>}
                            </InfoCard>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Cuentas de la misma persona (admin / profe / estudiante) */}
                {currentUser?.role === "superadmin" && <LinkedAccountsPanel userId={viewUser.user_id} />}

                {/* Enrollments (students only) */}
                {viewUser.role === "student" && (
                  <div data-testid="view-user-enrollments">
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="h-px flex-1 bg-slate-200" />
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {t("enrollments")}
                        {viewEnrollments.length > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-[10px]">
                            {viewEnrollments.length}
                          </span>
                        )}
                      </div>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    {loadingView ? (
                      <p className="text-xs text-slate-500 text-center py-4">{t("loading")}…</p>
                    ) : viewEnrollments.length === 0 ? (
                      <div className="text-center py-6 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                        <CalendarClock className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                        <p className="text-xs text-slate-500 italic">{t("view_no_enrollments")}</p>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {viewEnrollments.map((e) => {
                          const g = groupsMap[e.group_id];
                          const prog = g ? programsMap[g.program_id] : null;
                          return (
                            <div
                              key={e.id}
                              className="p-4 bg-white rounded-lg border border-slate-200 hover:border-brand-blue/40 hover:shadow-sm transition-all space-y-3"
                              data-testid={`view-enrollment-${e.id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="font-display font-bold text-base text-slate-900 leading-tight">
                                    {prog?.name || <em className="text-slate-400">—</em>}
                                  </div>
                                  {g?.name && (
                                    <div className="text-xs text-slate-500 mt-0.5">{g.name}</div>
                                  )}
                                </div>
                                {prog?.is_private && (
                                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                    {t("private")}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <Metric
                                  icon={<CalendarDays className="h-3 w-3" />}
                                  label={t("start_date")}
                                  value={e.start_date}
                                />
                                <Metric
                                  icon={<Wallet className="h-3 w-3" />}
                                  label={t("amount")}
                                  emptyText={t("no_amount")}
                                >
                                  {e.amount != null && e.amount !== ""
                                    ? <span>$ {Number(e.amount || 0).toFixed(2)} <span className="text-[10px] text-slate-500">USD</span></span>
                                    : null}
                                </Metric>
                              </div>
                              <div className="grid grid-cols-1 gap-2 text-xs pt-2 border-t border-slate-100">
                                <Metric
                                  icon={<CreditCard className="h-3 w-3" />}
                                  label={t("payment_link")}
                                  emptyText={t("no_payment_link")}
                                >
                                  {e.payment_link ? (
                                    <a
                                      href={e.payment_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-brand-blue hover:underline break-all"
                                    >
                                      <span className="truncate max-w-[280px]">{e.payment_link}</span>
                                      <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                                    </a>
                                  ) : null}
                                </Metric>
                                <Metric
                                  icon={<Landmark className="h-3 w-3" />}
                                  label={t("bank_account")}
                                  emptyText={t("no_bank_account")}
                                >
                                  {e.bank_account ? (
                                    <span className="font-mono text-[11px] text-slate-800">{bankLabelFor(e.bank_account)}</span>
                                  ) : null}
                                </Metric>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          <DialogFooter className="px-6 pb-5 pt-3 border-t border-slate-100 bg-slate-50/50">
            <Button variant="outline" onClick={() => setOpenView(false)} data-testid="view-close-button">{t("cancel")}</Button>
            {/* Teachers can view student profiles but MUST NOT edit them.
                The pencil in the row is already gated the same way. */}
            {viewUser && currentUser?.role !== "teacher" && (
              <Button
                onClick={() => { setOpenView(false); openEditDialog(viewUser); }}
                data-testid="view-edit-button"
                className="btn-brand"
              >
                <Pencil className="h-3.5 w-3.5" /> {t("edit")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick attendance dialog opened from the student row */}
      {attDialog && (
        <StudentAttendanceDialog
          open={!!attDialog}
          onOpenChange={(o) => { if (!o) setAttDialog(null); }}
          student={attDialog.student}
          group={attDialog.group}
          enrollment={attDialog.enrollment}
          totalPerCycle={attDialog.totalPerCycle}
        />
      )}

      <Dialog open={!!regalo} onOpenChange={(v) => !v && setRegalo(null)}>
        <DialogContent data-testid="grant-live-pass-dialog">
          <DialogHeader>
            <DialogTitle>Regalarle WSS Live a {regalo?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Termina su programa y sigue entrando a las clases en vivo gratis por el tiempo que
            le des. Al vencer, el sistema le emite la factura del mes con el enlace de pago.
            Conserva su cuenta, su historial y su código de ingreso.
          </p>
          <div className="mt-1">
            <label className="text-xs font-medium text-slate-600">Cuánta cortesía</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {[["1 semana", { days: 7 }], ["2 semanas", { days: 14 }],
                ["1 mes", { months: 1 }], ["2 meses", { months: 2 }]].map(([etiqueta, valor]) => {
                const elegido = (regalo?.days || 0) === (valor.days || 0)
                  && (regalo?.months || 0) === (valor.months || 0);
                return (
                  <button
                    key={etiqueta} type="button"
                    data-testid={`grant-live-pass-option-${valor.days || 0}-${valor.months || 0}`}
                    onClick={() => setRegalo({ ...regalo, days: valor.days || 0, months: valor.months || 0 })}
                    className={`text-sm rounded-lg border px-3 py-2 transition-colors ${
                      elegido ? "border-brand bg-brand/10 font-semibold text-brand"
                              : "border-slate-200 hover:bg-slate-50"}`}
                  >
                    {etiqueta}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Le llega un correo contándole el regalo y hasta cuándo lo tiene.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegalo(null)}>{t("cancel")}</Button>
            <Button onClick={regalarPase} data-testid="grant-live-pass-confirm">
              Darle el pase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small UI helpers (file-local) for the View dialog
function InfoCard({ icon, label, children, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function Metric({ icon, label, value, children, emptyText }) {
  const hasContent = children != null && children !== false;
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>
      <div className="text-slate-800">
        {children !== undefined ? (
          hasContent ? children : <em className="text-slate-400 not-italic">{emptyText || "—"}</em>
        ) : (
          value != null && value !== "" ? value : <em className="text-slate-400 not-italic">{emptyText || "—"}</em>
        )}
      </div>
    </div>
  );
}

function roleBadgeClasses(role) {
  switch (role) {
    case "superadmin": return "bg-red-100 text-red-700";
    case "academic_director": return "bg-purple-100 text-purple-700";
    case "teacher": return "bg-blue-100 text-blue-700";
    case "student": return "bg-emerald-100 text-emerald-700";
    default: return "bg-slate-100 text-slate-700";
  }
}
